import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, Tag, Button, Input, Select, Drawer,
    Descriptions, notification, Typography, Row, Col,
} from 'antd';
import {
    ReloadOutlined, EyeOutlined, SearchOutlined, LoadingOutlined,
    RiseOutlined, CalendarOutlined, HistoryOutlined,
    CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, StopOutlined,
} from '@ant-design/icons';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApi from '../../api/adminAxios';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const statusColor = { success: 'green', pending: 'orange', failed: 'red', cancelled: 'default' };

export default function AdminPayments() {
    const [payments, setPayments] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    const [perPage, setPerPage] = useState(10);
    const [drawer, setDrawer] = useState({ open: false, record: null });

    const fetchStats = () => {
        adminApi.get('/admin/payments/stats').then((r) => setStats(r.data.data)).catch(() => {});
    };

    const fetchPayments = useCallback(async (page = 1, searchVal = search, size = perPage) => {
        setLoading(true);
        try {
            const params = { page, per_page: size || 10 };
            if (status) params.status = status;
            if (searchVal) params.search = searchVal;
            const res = await adminApi.get('/admin/payments', { params });
            const d = res.data.data;
            setPayments(d.data);
            setPagination({ current: d.current_page, pageSize: d.per_page, total: d.total });
        } catch (_) {
            notification.error({ message: 'Failed to Load', description: 'Could not fetch payment records. Please try again.' });
        } finally {
            setLoading(false);
        }
    }, [status, search, perPage]);

    const handleSearch = (val) => {
        setSearch(val);
        fetchPayments(1, val);
    };

    const handlePerPageChange = (val) => {
        setPerPage(val);
        fetchPayments(1, search, val);
    };

    useEffect(() => {
        fetchStats();
        fetchPayments(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const openDetail = async (id) => {
        try {
            const res = await adminApi.get(`/admin/payments/${id}`);
            setDrawer({ open: true, record: res.data.data });
        } catch (_) {
            notification.error({ message: 'Failed to Load Detail', description: 'Could not fetch the payment details. Please try again.' });
        }
    };

    const statCards = [
        { label: 'Total Revenue',  value: stats ? `₹${Number(stats.total_revenue).toLocaleString('en-IN')}` : '—',        color: '#AD1457', bg: '#FCE4EC', icon: <RiseOutlined /> },
        { label: 'This Month',     value: stats ? `₹${Number(stats.revenue_this_month).toLocaleString('en-IN')}` : '—',   color: '#10B981', bg: '#D1FAE5', icon: <CalendarOutlined /> },
        { label: 'Last Month',     value: stats ? `₹${Number(stats.revenue_last_month).toLocaleString('en-IN')}` : '—',   color: '#3B82F6', bg: '#DBEAFE', icon: <HistoryOutlined /> },
    ];

    const statusIcons = {
        success:   { icon: <CheckCircleOutlined />,  color: '#10B981', bg: '#D1FAE5' },
        pending:   { icon: <ClockCircleOutlined />,  color: '#F59E0B', bg: '#FEF3C7' },
        failed:    { icon: <CloseCircleOutlined />,  color: '#EF4444', bg: '#FEE2E2' },
        cancelled: { icon: <StopOutlined />,         color: '#64748B', bg: '#F1F5F9' },
    };

    const columns = [
        {
            title: 'Order ID',
            dataIndex: 'order_id',
            render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
        },
        {
            title: 'User',
            render: (_, r) => r.user ? (
                <div>
                    <div style={{ fontWeight: 600 }}>{r.user.first_name} {r.user.last_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{r.user.email}</div>
                </div>
            ) : '—',
        },
        {
            title: 'Plan',
            render: (_, r) => r.subscription_plan?.name || '—',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            render: (v) => <Text strong>₹{Number(v).toLocaleString('en-IN')}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (v) => <Tag color={statusColor[v] || 'default'}>{v}</Tag>,
        },
        {
            title: 'Date',
            dataIndex: 'created_at',
            width: 110,
            render: (v) => dayjs(v).format('D MMM YYYY'),
        },
        {
            title: '',
            width: 60,
            render: (_, r) => (
                <Button type="text" size="small" icon={<EyeOutlined />}
                    onClick={() => openDetail(r.id)}
                    style={{ color: 'var(--color-primary)' }} />
            ),
        },
    ];

    return (
        <AdminLayout title="Payments">
            <div className="admin-page-header">
                <h1 className="admin-page-title">Payments</h1>
                <p className="admin-page-subtitle">Transaction history and revenue overview</p>
            </div>

            {/* Stat cards — same structure and breakpoints as dashboard */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {statCards.map((s) => (
                    <Col key={s.label} xs={24} sm={12} lg={6}>
                        <div className="admin-stat-card">
                            <div className="admin-stat-card-icon" style={{ background: s.bg, color: s.color }}>
                                {s.icon}
                            </div>
                            <div className="admin-stat-card-value">{s.value}</div>
                            <div className="admin-stat-card-label">{s.label}</div>
                        </div>
                    </Col>
                ))}
                {stats?.by_status?.map((s) => {
                    const meta = statusIcons[s.status] || { icon: <CheckCircleOutlined />, color: '#64748B', bg: '#F1F5F9' };
                    return (
                        <Col key={s.status} xs={24} sm={12} lg={6}>
                            <div className="admin-stat-card">
                                <div className="admin-stat-card-icon" style={{ background: meta.bg, color: meta.color }}>
                                    {meta.icon}
                                </div>
                                <div className="admin-stat-card-value">{s.count}</div>
                                <div className="admin-stat-card-label" style={{ textTransform: 'capitalize' }}>
                                    {s.status} · ₹{Number(s.total).toLocaleString('en-IN')}
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            <div className="admin-card">
                <div className="admin-card-header">
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flex: 1 }}>
                        <Input
                            placeholder="Search order ID, user, email..."
                            allowClear
                            prefix={<SearchOutlined style={{ color: 'var(--color-text-muted)' }} />}
                            value={search}
                            onChange={(e) => { if (!e.target.value) handleSearch(''); else setSearch(e.target.value); }}
                            onPressEnter={(e) => handleSearch(e.target.value)}
                            style={{ width: '100%', maxWidth: 280 }}
                        />
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={() => handleSearch(search)}
                            style={{ background: 'var(--gradient-primary)', border: 'none' }}
                        >
                            Search
                        </Button>
                        <Select value={status} onChange={(v) => setStatus(v)} style={{ width: 140 }}>
                            <Option value="">All Statuses</Option>
                            <Option value="success">Success</Option>
                            <Option value="pending">Pending</Option>
                            <Option value="failed">Failed</Option>
                            <Option value="cancelled">Cancelled</Option>
                        </Select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Per page:</span>
                        <Select
                            value={perPage}
                            onChange={handlePerPageChange}
                            style={{ width: 80 }}
                            options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: n }))}
                        />
                        <Button icon={<ReloadOutlined />} onClick={() => { fetchStats(); fetchPayments(pagination.current); }}>
                            Refresh
                        </Button>
                    </div>
                </div>

                <Table
                    className="admin-table"
                    dataSource={payments}
                    columns={columns}
                    rowKey="id"
                    loading={{ spinning: loading, indicator: <LoadingOutlined style={{ fontSize: 32, color: '#AD1457' }} spin /> }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        showQuickJumper: false,
                        showTotal: (t) => `${t} transactions`,
                        onChange: fetchPayments,
                    }}
                    scroll={{ x: 800 }}
                    size="middle"
                />
            </div>

            {/* Detail Drawer */}
            <Drawer
                title="Payment Detail"
                width={Math.min(440, window.innerWidth)}
                open={drawer.open}
                onClose={() => setDrawer({ open: false, record: null })}
            >
                {drawer.record && (() => {
                    const r = drawer.record;
                    return (
                        <div>
                            <Descriptions column={1} size="small" bordered>
                                <Descriptions.Item label="Order ID">
                                    <Text code>{r.order_id}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Transaction ID">
                                    {r.transaction_id || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <Tag color={statusColor[r.status]}>{r.status}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Amount">
                                    <Text strong>₹{Number(r.amount).toLocaleString('en-IN')}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Currency">{r.currency}</Descriptions.Item>
                                <Descriptions.Item label="Method">{r.payment_method || '—'}</Descriptions.Item>
                                <Descriptions.Item label="User">
                                    {r.user ? (
                                        <div>
                                            <div>{r.user.first_name} {r.user.last_name}</div>
                                            {r.user.email && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{r.user.email}</div>}
                                        </div>
                                    ) : '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Plan">
                                    {r.subscription_plan?.name || '—'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Paid At">
                                    {r.paid_at ? dayjs(r.paid_at).format('D MMM YYYY HH:mm') : '—'}
                                </Descriptions.Item>
                                {r.failure_reason && (
                                    <Descriptions.Item label="Failure Reason">
                                        <Text type="danger">{r.failure_reason}</Text>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </div>
                    );
                })()}
            </Drawer>
        </AdminLayout>
    );
}
